// ============================================================
// HOOKED — WYLLY'S BAIT & TACKLE  (the keeper is IN)
// The Outfitter's storefront + the fitting room, one panel.
// Ten slot tabs (tackle lane + the IE 6-slot wardrobe schema),
// a live AnglerVector preview wearing everything you equip,
// and the SCALES lane doing its one job.
//
// LAWS IN FORCE:
//   ONE SOURCE OF TRUTH — every item, price, stat, and earn
//     condition reads from tackle.js. Nothing item-shaped is
//     defined here.
//   PROVENANCE — EARNED items are auto-granted here the moment
//     their condition is met (checked against the ANGLER), and
//     they wear the brighter glow. BOUGHT wears the plain one.
//   DELTA WRITES — BUY_ITEM / EQUIP_ITEM through the reducer
//     only. The shop never touches state directly.
//   KEYFRAME LAW — no <style> blocks; core keyframes are
//     already head-mounted at App mount.
// ============================================================

import { useEffect, useMemo, useState } from "react";
import { GM, SD, WHARF_SLAB, facet, bevelPanel, textGlow } from "./theme";
import {
  OUTFITTER, SLOTS, RADIANCE, getItem, isUnlocked, earnMet, getOutfit,
} from "./tackle";
import { AnglerVector } from "./PlayerEntity";

export function Wyllys({ angler, dispatch, onClose }) {
  const [slotId, setSlotId] = useState("rod");
  const slot = SLOTS.find(s => s.id === slotId);
  const items = OUTFITTER[slotId] || [];
  const outfit = getOutfit(angler);

  // ── Auto-grant earned items whose condition is now met ─────
  // Checked against the ANGLER, granted with the brighter sigil.
  useEffect(() => {
    Object.values(OUTFITTER).flat().forEach(it => {
      if (it.earn && !isUnlocked(angler, it.id) && earnMet(angler, it)) {
        dispatch({ type: "UNLOCK_ITEM", itemId: it.id, source: "hooked:earned" });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc closes the shop
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const equippedId = angler[slot.kind]?.[slotId] ?? null;

  const handleItem = (it) => {
    if (isUnlocked(angler, it.id)) {
      dispatch({ type: "EQUIP_ITEM", kind: slot.kind, slot: slotId, itemId: it.id });
      return;
    }
    if (it.earn) return; // locked behind the water — no coin opens it
    if (angler.scales >= (it.price ?? 0)) {
      dispatch({ type: "BUY_ITEM", item: it });
      dispatch({ type: "EQUIP_ITEM", kind: slot.kind, slot: slotId, itemId: it.id });
    }
  };

  const titleItem = getItem(angler.wardrobe?.title);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(1,2,8,0.78)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "fadeUp 0.22s ease",
    }}>
      <div style={{
        width: "min(880px, 96vw)", height: "min(620px, 92vh)",
        background: WHARF_SLAB, clipPath: facet(16),
        ...bevelPanel(GM.gold),
        display: "flex", flexDirection: "column",
        fontFamily: GM.fontDisplay, overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 20px 10px",
          borderBottom: `1px solid ${GM.inkBrown}`,
        }}>
          <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 3, color: GM.goldBright, ...textGlow(GM.gold) }}>
            ◆ WYLLY'S BAIT &amp; TACKLE
          </div>
          <div style={{ flex: 1 }} />
          <div style={{
            fontSize: 12, fontWeight: 900, letterSpacing: 1.5, color: GM.goldBright,
            padding: "5px 14px", borderRadius: 999,
            background: `linear-gradient(180deg, ${GM.panelHi}, ${GM.pageDeep})`,
            border: `1px solid ${GM.gold}55`, ...textGlow(GM.gold),
          }}>
            ❖ {angler.scales} SCALES
          </div>
          <div onPointerDown={onClose} style={{
            cursor: "pointer", fontSize: 14, color: GM.inkDim,
            padding: "4px 10px", borderRadius: 8,
            border: `1px solid ${GM.inkBrown}`, userSelect: "none",
          }}>✕</div>
        </div>

        {/* ── Body: preview | tabs + grid ── */}
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>

          {/* Fitting room — the live preview */}
          <div style={{
            width: 190, flexShrink: 0,
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: "18px 10px", gap: 8,
            borderRight: `1px solid ${GM.inkBrown}`,
            background: `radial-gradient(120% 90% at 50% 20%, ${SD.void3}44, transparent 70%)`,
          }}>
            <div style={{ position: "relative", width: 150, height: 150 }}>
              {outfit.aura && (
                <div style={{
                  position: "absolute", left: "50%", top: "62%",
                  width: 150, height: 90, transform: "translate(-50%,-50%)",
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${outfit.aura}55 0%, ${outfit.aura}22 45%, transparent 72%)`,
                  filter: "blur(4px)",
                  animation: "moonShimmer 3.5s ease-in-out infinite",
                }} />
              )}
              <AnglerVector mode="walk" isIdle size={150} outfit={outfit} />
            </div>
            {/* the dock circle underfoot */}
            <div style={{
              width: 110, height: 16, marginTop: -14, borderRadius: "50%",
              background: `radial-gradient(ellipse, ${GM.rope}33, transparent 70%)`,
            }} />
            {titleItem && (
              <div style={{
                fontSize: 9, letterSpacing: 2, textAlign: "center",
                color: RADIANCE[titleItem.radiance]?.color || GM.silver,
                ...textGlow(RADIANCE[titleItem.radiance]?.color || GM.silver),
              }}>
                {titleItem.glyph} {titleItem.name}
              </div>
            )}
            <div style={{
              marginTop: "auto", fontSize: 9, color: GM.inkDim, fontFamily: GM.fontBody,
              fontStyle: "italic", textAlign: "center", lineHeight: 1.5, padding: "0 6px",
            }}>
              Wylly polishes a ferrule and watches you decide.
            </div>
          </div>

          {/* Shelves */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

            {/* slot tabs */}
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 5,
              padding: "10px 14px 8px",
              borderBottom: `1px solid ${GM.inkBrown}`,
            }}>
              {SLOTS.map(s => {
                const active = s.id === slotId;
                return (
                  <div key={s.id} onPointerDown={() => setSlotId(s.id)} style={{
                    padding: "5px 11px", borderRadius: 999, cursor: "pointer",
                    fontSize: 9, letterSpacing: 1.5, userSelect: "none",
                    fontFamily: GM.fontUI, fontWeight: active ? 900 : 400,
                    color: active ? GM.pageDeep : GM.inkDim,
                    background: active
                      ? `linear-gradient(180deg, ${GM.goldPale}, ${GM.gold})`
                      : `linear-gradient(180deg, ${GM.panelHi}, ${GM.pageDeep})`,
                    border: `1px solid ${active ? GM.goldBright : GM.inkBrown}`,
                    boxShadow: active ? `0 2px 8px ${GM.gold}55, inset 0 1px 0 #fff8` : "none",
                    transition: "all 0.12s",
                  }}>
                    {s.label}
                  </div>
                );
              })}
            </div>

            {/* item grid */}
            <div style={{
              flex: 1, overflowY: "auto", padding: 14,
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))",
              gap: 10, alignContent: "start",
            }}>
              {slot.optional && (
                <NoneCard
                  active={equippedId === null}
                  onPick={() => dispatch({ type: "EQUIP_ITEM", kind: slot.kind, slot: slotId, itemId: null })}
                />
              )}
              {items.map(it => (
                <ItemCard
                  key={it.id}
                  item={it}
                  owned={isUnlocked(angler, it.id)}
                  equipped={equippedId === it.id}
                  affordable={angler.scales >= (it.price ?? 0)}
                  onPick={() => handleItem(it)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* footer hint */}
        <div style={{
          padding: "7px 0 9px", textAlign: "center",
          fontSize: 8.5, letterSpacing: 2, color: GM.inkDim, fontFamily: GM.fontUI,
          borderTop: `1px solid ${GM.inkBrown}`,
        }}>
          TAP TO BUY &amp; WEAR · EARNED PIECES UNLOCK ON THE WATER · ESC TO LEAVE
        </div>
      </div>
    </div>
  );
}

// ── Stat line — WOVEN labels, derived from item fields ───────
function statText(it) {
  const bits = [];
  if (it.lyt)        bits.push(`+${it.lyt} HOOK`);
  if (it.lyf)        bits.push(`+${it.lyf} LINE`);
  if (it.shoreReach) bits.push(it.shoreReach > 100 ? "SHORE REACHES ANYWHERE" : "SHORE CASTS RUN DEEPER");
  if (it.ringSlow && it.ringSlow > 1) bits.push("SLOWER STRIKE RING");
  if (it.waitMult && it.waitMult < 1) bits.push("SHORTER WAITS");
  if (it.tierShift)  bits.push("RICHER WATERS");
  if (it.ilkBias)    bits.push(`DRAWS ${it.ilkBias.toUpperCase()}-WATER FISH`);
  return bits.join(" · ");
}

function ItemCard({ item, owned, equipped, affordable, onPick }) {
  const rad = RADIANCE[item.radiance] || RADIANCE.common;
  const lockedEarn = !owned && !!item.earn;
  const buyable = !owned && !item.earn && affordable;
  const dead = !owned && !item.earn && !affordable;
  const stat = statText(item);

  return (
    <div onPointerDown={onPick} style={{
      position: "relative",
      background: equipped
        ? `linear-gradient(170deg, ${rad.color}1c, ${GM.pageDeep})`
        : `linear-gradient(170deg, ${GM.panelHi}, ${GM.pageDeep})`,
      clipPath: facet(9),
      border: "none",
      boxShadow: [
        `inset 0 0 0 1px ${equipped ? rad.color : rad.color + "55"}`,
        equipped ? `0 0 16px ${rad.color}44` : "0 3px 10px rgba(0,0,0,0.5)",
        `inset 0 1px 0 ${GM.goldPale}14`,
      ].join(", "),
      padding: "10px 11px 9px",
      cursor: dead ? "default" : "pointer",
      opacity: dead ? 0.55 : lockedEarn ? 0.85 : 1,
      userSelect: "none",
      display: "flex", flexDirection: "column", gap: 4,
      transition: "box-shadow 0.15s, opacity 0.15s",
    }}>
      {/* radiance ribbon */}
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 17,
          background: `radial-gradient(circle at 40% 30%, ${rad.color}30, ${GM.pageDeep})`,
          boxShadow: `inset 0 0 0 1px ${rad.color}66`,
          filter: lockedEarn ? "grayscale(0.6)" : "none",
        }}>
          {lockedEarn ? "🔒" : item.glyph}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 900, letterSpacing: 0.8,
            color: GM.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {item.name}
          </div>
          <div style={{ fontSize: 7.5, letterSpacing: 1.5, color: rad.color, ...textGlow(rad.color) }}>
            {rad.label}
          </div>
        </div>
      </div>

      {/* flavor */}
      <div style={{
        fontSize: 8.5, lineHeight: 1.45, color: GM.inkDim,
        fontFamily: GM.fontBody, fontStyle: "italic", minHeight: 24,
      }}>
        {item.desc}
      </div>

      {/* stat line — functionality wears its numbers openly */}
      {stat && (
        <div style={{ fontSize: 7.5, letterSpacing: 1, color: SD.tealLight, fontFamily: GM.fontUI }}>
          {stat}
        </div>
      )}

      {/* footer: state */}
      <div style={{
        marginTop: "auto", paddingTop: 3,
        fontSize: 8.5, letterSpacing: 1.5, fontFamily: GM.fontUI, fontWeight: 900,
        color: equipped ? rad.color
          : owned ? SD.tealLight
          : lockedEarn ? GM.inkDim
          : buyable ? GM.goldBright
          : GM.inkDim,
      }}>
        {equipped ? "✦ WORN"
          : owned ? "OWNED — TAP TO WEAR"
          : lockedEarn ? `🔒 ${item.earn.text?.toUpperCase() || "EARNED ON THE WATER"}`
          : `❖ ${item.price}`}
      </div>
    </div>
  );
}

// ── The bare option for optional slots ───────────────────────
function NoneCard({ active, onPick }) {
  return (
    <div onPointerDown={onPick} style={{
      background: `linear-gradient(170deg, ${GM.panelHi}, ${GM.pageDeep})`,
      clipPath: facet(9),
      boxShadow: [
        `inset 0 0 0 1px ${active ? GM.silver : GM.inkBrown}`,
        active ? `0 0 12px ${GM.silver}33` : "0 3px 10px rgba(0,0,0,0.5)",
      ].join(", "),
      padding: "10px 11px 9px", cursor: "pointer", userSelect: "none",
      display: "flex", flexDirection: "column", gap: 4, minHeight: 96,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1, color: GM.inkDim }}>— NONE —</div>
      <div style={{ fontSize: 8.5, color: GM.inkDim, fontStyle: "italic", fontFamily: GM.fontBody }}>
        The lake asks for nothing.
      </div>
      <div style={{
        marginTop: "auto", fontSize: 8.5, letterSpacing: 1.5, fontWeight: 900,
        fontFamily: GM.fontUI, color: active ? GM.silverBright : GM.inkDim,
      }}>
        {active ? "✦ WORN" : "TAP TO CLEAR"}
      </div>
    </div>
  );
}
